<template>
  <p
    class="novel-paragraph"
    :class="classObj"
    :style="style"
    :id="textBlock.paragraphId"
    ref="parent"
    @copy.prevent
  >
    <span class="relative">
      <span v-if="bookmarked && showBookmarks" class="novel-bookmark">
        <IconBookmarkFilled size="1.25em" />
      </span>
      <template v-if="typeof content === 'string'">
        {{ content }}
      </template>
      <template v-else-if="content.length">
        <NovelRenderRichText
          v-for="block in content"
          :textBlock="block"
          :overrideFont
          :language
          text-render
          @footnote="(v) => emit('footnote', v)"
        />
      </template>
      <TheNuxtLink
        class="novel-footnote-number"
        v-if="textBlock.footnoteMeta"
        :id="`footnote-number-${textBlock.footnoteMeta.id}`"
        @click.stop="emit('footnote', textBlock.footnoteMeta.id)"
      >
        <sup>{{ textBlock.footnoteMeta.id.split("=").at(-1) }}</sup>
      </TheNuxtLink>
      <button
        v-if="comments && showComments"
        class="novel-comment-counter"
        @click.stop="emit('comment', textBlock.paragraphId)"
      >
        <IconMessageCircle size="1.25em" class="mr-0.5" /><span>{{
          comments
        }}</span>
      </button>
    </span>
  </p>
</template>

<script setup lang="ts">
import { getContent, type TextBlock } from "~/src/novel/core";
import { IconMessageCircle, IconBookmarkFilled } from "@tabler/icons-vue";
import { getComputedStyles } from "~/src/novel/render";
import type { AvailableLanguages } from "~/src/api";
import { scrollElementIntoViewById } from "~/src/novel/reader";

defineOptions({
  name: "novel-paragraph-global",
});

const { textBlock, overrideFont, language } = defineProps<{
  textBlock: TextBlock;
  comments?: number;
  bookmarked?: boolean;
  language?: AvailableLanguages;

  overrideFont?: boolean;
  showComments?: boolean;
  showBookmarks?: boolean;
}>();

const emit = defineEmits<{
  (e: "comment", id?: string): void;
  (e: "footnote", id: string): void;
}>();

const parent = useTemplateRef<HTMLParagraphElement>("parent");
const content = computed(() => getContent(textBlock));
const { style, classObj } = getComputedStyles(
  () => textBlock,
  () => overrideFont,
  () => language,
);
</script>

<style lang="postcss">
@import url("assets/css/novel/style.pcss");
@import url("assets/css/novel/format.pcss");
</style>
